import React from 'react';
import { useCreateBranchMutation } from '@/api/queries';
import type { CreateOutcome } from '@/utils';
import type { BranchFormSubmit } from './components/branchFormModal/useBranchForm';

/**
 * `CreateOutcome` plus the created branch's id on success: the onboarding
 * Branch step selects the branch it has just created. A failure also keeps
 * the refusal's `code` (when the server sends one) and the HTTP `status`, so
 * a caller can say it in its own words instead of the server's sentence.
 * The other callers ignore both, so the additions are additive (see
 * `CreateOutcome`).
 */
export type BranchCreateOutcome =
    | { ok: true; branchId: string }
    | (Extract<CreateOutcome, { ok: false }> & {
          code?: string;
          status?: number;
      });

/**
 * Creating a branch — the one definition, shared by the branches page and the
 * Control Center's "Add branch".
 *
 * See `@/pages/brands/useCreateBrand` for why the create path lives in a hook
 * of its own. `area` has already been parsed to a number or `null` by
 * `useBranchForm`, and `isMain` is always sent: the server demotes the previous
 * main branch in the same transaction, so there is nothing for either caller to
 * coordinate.
 */
export const useCreateBranch = () => {
    const createMutation = useCreateBranchMutation();

    const create = React.useCallback(
        async (input: BranchFormSubmit): Promise<BranchCreateOutcome> => {
            const result = await createMutation.mutateAsync({
                name: input.name,
                address: input.address,
                isMain: input.isMain,
                area: input.area,
                imageUpload: input.imageUpload,
            });

            return result.success
                ? { ok: true, branchId: result.response.id }
                : {
                      ok: false,
                      message:
                          result.error.message ?? 'Failed to create branch.',
                      code: result.error.code,
                      status: result.error.status,
                  };
        },
        [createMutation],
    );

    return { create, isCreating: createMutation.isPending };
};
